// =============================================================================
// Admin Analytics — lightweight CSS charts + shared states
// =============================================================================
// Mosaic ships no bar/column chart, so these are purpose-built but stay within
// the design register: warm-black alpha, limestone/teal palette, snappy CSS
// transitions (no animation library in the repo). Each bar animates in on mount.
// =============================================================================
import { clsx } from "clsx";

import { Label, Tooltip } from "@fronterahealth/frontera-ui-components";

const RIO_TEAL = "#1C4A50";

// ----- Table cell empty state -------------------------------------------------

export const EmptyCell: React.FC = () => <span className="text-limestone-400">n/a</span>;

/** Returns a truncated span with a hover title; falls back to a muted "n/a" placeholder. */
export const cellText = (v: string | null | undefined): React.ReactNode =>
  v ? (
    <span className="block truncate" title={v}>
      {v}
    </span>
  ) : (
    <EmptyCell />
  );

// ----- Empty / loading --------------------------------------------------------

const WidgetEmptyState: React.FC<{ message?: string }> = ({ message = "No data for this range" }) => (
  <div className="flex h-32 items-center justify-center rounded-md border border-dashed border-limestone-100">
    <Label size="extra-small" colorType="secondary">
      {message}
    </Label>
  </div>
);

const SKELETON_KEYS = ["sk-0", "sk-1", "sk-2", "sk-3", "sk-4", "sk-5", "sk-6", "sk-7", "sk-8", "sk-9"];

export const ChartSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="flex flex-col gap-3 py-1">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={SKELETON_KEYS[i]} className="flex items-center gap-3">
        <div className="h-3 w-24 animate-pulse rounded bg-limestone-100" />
        <div className="h-3 flex-1 animate-pulse rounded bg-limestone-100" style={{ opacity: 1 - i * 0.12 }} />
      </div>
    ))}
  </div>
);

// ----- Ranked horizontal bar list (Reports by user, etc.) --------------------

interface BarListItem {
  label: string;
  value: number;
  /** Optional comparison/secondary value shown muted after the primary value. */
  secondary?: number;
  /** Stable identity when labels can repeat (e.g. two providers with the same name). */
  id?: string;
  /** Per-item click handler; takes precedence over the list's `onItemClick`. */
  onClick?: () => void;
}

interface BarListProps {
  items: BarListItem[];
  color?: string;
  /** Cap the number of visible rows; the rest are summarized in a footer line. */
  maxRows?: number;
  /** How many items exist in total, when `items` is only the top of the list. Drives the "+N more" line. */
  totalCount?: number;
  valueSuffix?: string;
  /** When set, each bar is clickable — e.g. to open the raw rows behind that item. */
  onItemClick?: (label: string) => void;
}

interface BarListRowProps {
  item: BarListItem;
  index: number;
  max: number;
  color: string;
  valueSuffix?: string;
  onItemClick?: (label: string) => void;
}

const BarListRow: React.FC<BarListRowProps> = ({ item, index, max, color, valueSuffix, onItemClick }) => {
  const pct = Math.max(2, (item.value / max) * 100);
  const displayLabel = item.label || <EmptyCell />;
  const activate = item.onClick ?? (onItemClick ? () => onItemClick(item.label) : undefined);
  const clickable = Boolean(activate);
  const onKey = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && activate) {
      e.preventDefault();
      activate();
    }
  };

  return (
    <div key={item.id ?? (item.label || index)} className="flex items-center gap-3">
      {clickable ? (
        <button
          type="button"
          onClick={activate}
          onKeyDown={onKey}
          title={item.label ? `View reports for ${item.label}` : undefined}
          className="w-28 shrink-0 cursor-pointer truncate text-right hover:underline"
        >
          <Label size="extra-small" colorType="primary" className="text-gray-700 hover:text-primary">
            {displayLabel}
          </Label>
        </button>
      ) : (
        <div className="w-28 shrink-0 truncate text-right" title={item.label || undefined}>
          <Label size="extra-small" colorType="primary" className="text-gray-700">
            {displayLabel}
          </Label>
        </div>
      )}
      <div className="relative h-5 flex-1 overflow-hidden rounded-sm bg-limestone-50">
        {clickable ? (
          <button
            type="button"
            onClick={activate}
            onKeyDown={onKey}
            title={`View reports for ${item.label}`}
            className="h-full w-full rounded-sm p-0 transition-[width,filter] duration-500 ease-out hover:brightness-90"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        ) : (
          <div
            className="h-full rounded-sm transition-[width,filter] duration-500 ease-out"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        )}
      </div>
      <div className="flex w-16 shrink-0 items-baseline justify-end gap-1">
        <Label size="extra-small" colorType="primary">
          {item.value.toLocaleString()}
          {valueSuffix ? <span className="text-text-secondary"> {valueSuffix}</span> : null}
        </Label>
        {typeof item.secondary === "number" && (
          <span className="text-[10px] text-text-secondary">({item.secondary})</span>
        )}
      </div>
    </div>
  );
};

export const BarList: React.FC<BarListProps> = ({
  items,
  color = RIO_TEAL,
  maxRows,
  totalCount,
  valueSuffix,
  onItemClick,
}) => {
  if (!items.length) return <WidgetEmptyState />;
  const max = Math.max(...items.map((i) => i.value), 1);
  const visible = maxRows ? items.slice(0, maxRows) : items;
  const hiddenCount = Math.max(0, (totalCount ?? items.length) - visible.length);

  return (
    <div className="flex flex-col gap-2.5">
      {visible.map((item, i) => (
        <BarListRow
          key={item.id ?? (item.label || i)}
          item={item}
          index={i}
          max={max}
          color={color}
          valueSuffix={valueSuffix}
          onItemClick={onItemClick}
        />
      ))}
      {hiddenCount > 0 && (
        <Label size="extra-small" colorType="secondary" className="pl-[124px]">
          + {hiddenCount} more — open Raw Data to see all
        </Label>
      )}
    </div>
  );
};

// ----- Status stacked bar (Reports by status — stacked view) -----------------
// Extracted from MetricCard so ReportsByStatusWidget can swap chart content
// without remounting the outer WidgetContainer (prevents layout jump).

interface StatusStackedBarItem {
  label: string;
  value: number;
  color: string;
  definition?: string;
  onItemClick?: () => void;
}

interface StatusStackedBarProps {
  items: StatusStackedBarItem[];
}

export const StatusStackedBar: React.FC<StatusStackedBarProps> = ({ items }) => {
  const total = items.reduce((sum, i) => sum + i.value, 0);
  const nonZero = items.filter((i) => i.value > 0);
  const roundedRadius = "9999px";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="flex h-2 w-full max-w-full overflow-hidden rounded-sm bg-gray-100">
          {nonZero.map((item, index, arr) => {
            const pct = total === 0 ? 0 : (item.value / total) * 100;
            const isFirst = index === 0;
            const isLast = index === arr.length - 1;
            return (
              <div
                key={item.label}
                className="h-full transition-[width] duration-500 ease-out"
                style={{
                  width: `${pct}%`,
                  backgroundColor: item.color,
                  marginRight: isLast ? "0" : "2px",
                  borderTopLeftRadius: isFirst ? roundedRadius : "0",
                  borderBottomLeftRadius: isFirst ? roundedRadius : "0",
                  borderTopRightRadius: isLast ? roundedRadius : "0",
                  borderBottomRightRadius: isLast ? roundedRadius : "0",
                }}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-1">
          <Label size="extra-small">Total:</Label>
          <Label size="extra-small" colorType="primary">
            {total}
          </Label>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        {items.map((item) => {
          const pct = total === 0 ? 0 : Math.round((item.value / total) * 100);
          const clickable = Boolean(item.onItemClick) && item.value > 0;
          const Tag = clickable ? "button" : "div";
          return (
            <Tag
              key={item.label}
              type={clickable ? "button" : undefined}
              className={clsx(
                "grid w-full items-baseline rounded-sm",
                clickable && "cursor-pointer hover:bg-limestone-50",
              )}
              style={{ gridTemplateColumns: "1fr 5.5rem" }}
              onClick={clickable ? item.onItemClick : undefined}
              onKeyDown={
                clickable
                  ? (e: React.KeyboardEvent) =>
                      (e.key === "Enter" || e.key === " ") && (e.preventDefault(), item.onItemClick!())
                  : undefined
              }
            >
              <div className="flex items-center gap-2">
                <span className="inline-block h-1 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                {item.definition ? (
                  <Tooltip text={item.definition} showInfoIcon={false}>
                    <Label size="extra-small" colorType="primary" className="cursor-default text-gray-700">
                      {item.label}
                    </Label>
                  </Tooltip>
                ) : (
                  <Label size="extra-small" colorType="primary" className="text-gray-700">
                    {item.label}
                  </Label>
                )}
              </div>
              <div className="grid items-baseline" style={{ gridTemplateColumns: "2rem 3.5rem" }}>
                <span className="text-right tabular-nums text-[12px] text-gray-700">{item.value}</span>
                <span className="text-right tabular-nums text-[12px] text-limestone-500">({pct}%)</span>
              </div>
            </Tag>
          );
        })}
      </div>
    </div>
  );
};

// ----- Status bar list (Reports by status — bar view) -------------------------
// Like BarList but each item carries its own color (matching the status tag
// palette) and the right-side meta is "count · pct%" rather than a raw count.
// Always renders every item even when value === 0 so all statuses stay visible.

interface StatusBarListItem {
  label: string;
  value: number;
  color: string;
  /** Shown in a tooltip on hover — used for status definitions. */
  definition?: string;
  onItemClick?: () => void;
}

interface StatusBarListProps {
  items: StatusBarListItem[];
  total: number;
}

export const StatusBarList: React.FC<StatusBarListProps> = ({ items, total }) => {
  if (!items.length) return <WidgetEmptyState />;
  const max = Math.max(...items.map((i) => i.value), 1);

  return (
    <div className="flex flex-col gap-2.5">
      {items.map((item) => {
        const pct = total === 0 ? 0 : Math.round((item.value / total) * 100);
        const barPct = item.value > 0 ? Math.max(2, (item.value / max) * 100) : 0;
        const clickable = Boolean(item.onItemClick) && item.value > 0;
        const activate = clickable ? item.onItemClick : undefined;
        const onKey = activate
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                activate();
              }
            }
          : undefined;

        const BarFill = (clickable ? "button" : "div") as "button" | "div";
        return (
          <div key={item.label} className="flex items-center gap-3">
            <div className="w-28 shrink-0 truncate text-right">
              {item.definition ? (
                <Tooltip text={item.definition} showInfoIcon={false}>
                  <span className="cursor-default text-[12px] text-gray-700">{item.label}</span>
                </Tooltip>
              ) : (
                <span className="text-[12px] text-gray-700">{item.label}</span>
              )}
            </div>
            <div className="relative h-5 flex-1 overflow-hidden rounded-sm bg-limestone-50">
              <BarFill
                type={clickable ? "button" : undefined}
                className={clsx(
                  "h-full rounded-sm transition-[width,filter] duration-500 ease-out",
                  clickable && "cursor-pointer hover:brightness-90",
                )}
                onClick={activate}
                onKeyDown={onKey}
                title={clickable ? `View ${item.label} reports` : undefined}
                style={{ width: `${barPct}%`, backgroundColor: item.color }}
              />
            </div>
            <div className="w-20 shrink-0 text-right">
              <span className="text-[13px] text-limestone-700">
                {item.value.toLocaleString()} · {pct}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ----- Vertical column chart (Submission trend) ------------------------------

interface ColumnChartPoint {
  label: string;
  value: number;
  /** Optional stable id (e.g. ISO week start) passed back on click. */
  id?: string;
}

interface ColumnChartProps {
  points: ColumnChartPoint[];
  color?: string;
  /** When set, each column is clickable — e.g. to open the raw rows for that period. */
  onPointClick?: (point: ColumnChartPoint) => void;
}

export const ColumnChart: React.FC<ColumnChartProps> = ({ points, color = RIO_TEAL, onPointClick }) => {
  if (!points.length) return <WidgetEmptyState />;
  const max = Math.max(...points.map((p) => p.value), 1);
  const clickable = Boolean(onPointClick);
  const ColItem = (clickable ? "button" : "div") as "button" | "div";

  return (
    <div className="flex h-48 gap-2 overflow-x-auto pb-1">
      {points.map((p) => {
        const heightPct = Math.max(3, (p.value / max) * 100);
        return (
          <ColItem
            key={p.label}
            type={clickable ? "button" : undefined}
            className={clsx(
              "group flex h-full min-w-[34px] flex-1 flex-col items-center gap-2 rounded-sm",
              clickable && "cursor-pointer hover:bg-limestone-50",
            )}
            onClick={onPointClick ? () => onPointClick(p) : undefined}
            onKeyDown={
              clickable
                ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onPointClick!(p))
                : undefined
            }
            title={clickable ? `View reports submitted in ${p.label}` : undefined}
          >
            <div className="flex min-h-0 w-full flex-1 items-end justify-center">
              <div
                className="relative flex w-full max-w-[44px] items-start justify-center rounded-t-sm transition-[height,filter] duration-500 ease-out group-hover:brightness-95"
                style={{ height: `${heightPct}%`, backgroundColor: color }}
              >
                <span
                  className={clsx(
                    "absolute -top-5 text-[11px] font-medium text-gray-700 opacity-0 transition-opacity",
                    "group-hover:opacity-100",
                  )}
                >
                  {p.value}
                </span>
              </div>
            </div>
            <Label size="extra-small" colorType="secondary" className="whitespace-nowrap">
              {p.label}
            </Label>
          </ColItem>
        );
      })}
    </div>
  );
};
